import { useMemo } from 'react';

import {
  ScanDataMissingStoreStoreLevelDetailsFragment,
  ScanDataReimbursementsStoreLevelDetailsFragment,
  ScanDataReportedTransactionsStoreLevelDetailsFragment,
  ScanDataSubmittedTransactionsStoreLevelDetailsFragment,
} from '@occc/graphql';

import { UseScanDataStoreDetailsTableSearchAndPaginationReturn } from './use-scan-data-store-details-table-search-and-pagination.hook';

import { LoadedDataWithPagination } from '../../../scan-data.types';
import { useScanDataSubmittedTransactionsStoreLevelDetails } from '../../../hooks/use-scan-data-submitted-transactions-store-level-details.hook';
import { useScanDataMissingStoresStoreLevelDetails } from '../../../hooks/use-scan-data-missing-stores-store-level-details.hook';
import { useScanDataReimbursementsStoreLevelDetails } from '../../../hooks/use-scan-data-reimbursements-store-level-details.hook';
import { useScanDataReportedTransactionsStoreLevelDetails } from '../../../hooks/use-scan-data-reported-transactions-store-level-details.hook';

export type UseScanDataStoreDetailsTableDataReturn = {
  submittedTransactionsTableData: LoadedDataWithPagination<
    ScanDataSubmittedTransactionsStoreLevelDetailsFragment[]
  >;
  missingStoresTableData: LoadedDataWithPagination<ScanDataMissingStoreStoreLevelDetailsFragment[]>;
  reimbursementsTableData: LoadedDataWithPagination<
    ScanDataReimbursementsStoreLevelDetailsFragment[]
  >;
  reportedTransactionsTableData: LoadedDataWithPagination<
    ScanDataReportedTransactionsStoreLevelDetailsFragment[]
  >;
};

export const useScanDataStoreDetailsTableData = (
  submissionDateEquals: string,
  searchAndPagination: UseScanDataStoreDetailsTableSearchAndPaginationReturn,
): UseScanDataStoreDetailsTableDataReturn => {
  const { paginationInfoByMetric, pageChangeHandlersByMetric, searchValueByMetric } =
    searchAndPagination;

  const scanDataSubmittedTransactions = useScanDataSubmittedTransactionsStoreLevelDetails({
    submissionDateEquals,
    paginationInfo: paginationInfoByMetric['stores-submitted'],
    onPageChange: pageChangeHandlersByMetric['stores-submitted'],
    search: searchValueByMetric['stores-submitted'],
  });

  const scanDataMissingStores = useScanDataMissingStoresStoreLevelDetails({
    submissionDateEquals,
    paginationInfo: paginationInfoByMetric['missing-stores'],
    onPageChange: pageChangeHandlersByMetric['missing-stores'],
    search: searchValueByMetric['missing-stores'],
  });

  const scanDataReimbursements = useScanDataReimbursementsStoreLevelDetails({
    submissionDateEquals,
    paginationInfo: paginationInfoByMetric.reimbursements,
    onPageChange: pageChangeHandlersByMetric.reimbursements,
    search: searchValueByMetric.reimbursements,
  });

  const scanDataReportedTransactions = useScanDataReportedTransactionsStoreLevelDetails({
    submissionDateEquals,
    paginationInfo: paginationInfoByMetric['transactions-reported'],
    onPageChange: pageChangeHandlersByMetric['transactions-reported'],
    search: searchValueByMetric['transactions-reported'],
  });

  const submittedTransactionsTableData: LoadedDataWithPagination<
    ScanDataSubmittedTransactionsStoreLevelDetailsFragment[]
  > = useMemo(
    () => ({
      data: scanDataSubmittedTransactions.data?.scanDataSubmittedTransactionsStoreLevelDetails.edges.map(
        (edge) => edge.node,
      ),
      loading: scanDataSubmittedTransactions.loading,
      error: scanDataSubmittedTransactions.error,
      paginationProps: scanDataSubmittedTransactions.paginationProps,
    }),
    [
      scanDataSubmittedTransactions.data?.scanDataSubmittedTransactionsStoreLevelDetails.edges,
      scanDataSubmittedTransactions.loading,
      scanDataSubmittedTransactions.error,
      scanDataSubmittedTransactions.paginationProps,
    ],
  );

  const missingStoresTableData: LoadedDataWithPagination<
    ScanDataMissingStoreStoreLevelDetailsFragment[]
  > = useMemo(
    () => ({
      data: scanDataMissingStores.data?.scanDataMissingStoresStoreLevelDetails.edges.map(
        (edge) => edge.node,
      ),
      loading: scanDataMissingStores.loading,
      error: scanDataMissingStores.error,
      paginationProps: scanDataMissingStores.paginationProps,
    }),
    [
      scanDataMissingStores.data?.scanDataMissingStoresStoreLevelDetails.edges,
      scanDataMissingStores.loading,
      scanDataMissingStores.error,
      scanDataMissingStores.paginationProps,
    ],
  );

  const reimbursementsTableData: LoadedDataWithPagination<
    ScanDataReimbursementsStoreLevelDetailsFragment[]
  > = useMemo(
    () => ({
      data: scanDataReimbursements.data?.scanDataReimbursementsStoreLevelDetails.edges.map(
        (edge) => edge.node,
      ),
      loading: scanDataReimbursements.loading,
      error: scanDataReimbursements.error,
      paginationProps: scanDataReimbursements.paginationProps,
    }),
    [
      scanDataReimbursements.data?.scanDataReimbursementsStoreLevelDetails.edges,
      scanDataReimbursements.loading,
      scanDataReimbursements.error,
      scanDataReimbursements.paginationProps,
    ],
  );

  const reportedTransactionsTableData: LoadedDataWithPagination<
    ScanDataReportedTransactionsStoreLevelDetailsFragment[]
  > = useMemo(
    () => ({
      data: scanDataReportedTransactions.data?.scanDataReportedTransactionsStoreLevelDetails.edges.map(
        (edge) => edge.node,
      ),
      loading: scanDataReportedTransactions.loading,
      error: scanDataReportedTransactions.error,
      paginationProps: scanDataReportedTransactions.paginationProps,
    }),
    [
      scanDataReportedTransactions.data?.scanDataReportedTransactionsStoreLevelDetails.edges,
      scanDataReportedTransactions.loading,
      scanDataReportedTransactions.error,
      scanDataReportedTransactions.paginationProps,
    ],
  );

  return {
    submittedTransactionsTableData,
    missingStoresTableData,
    reimbursementsTableData,
    reportedTransactionsTableData,
  };
};
